import { observer } from "mobx-react-lite";
import { ElementRef, useCallback, useRef, useState } from "react";
import { numberToStringWithSpaces, sanitizeInput } from "../../../helpers/helpers";
import { ArrowDown, ArrowUp, Calculator, Check, PencilSquare, X } from "react-bootstrap-icons";
import { Tooltip as ReactTooltip } from "react-tooltip";

interface AnnuityCalcTableRowProps {
    toggleExpanded?: () => void;
    yearExpanded: boolean;
    nrOfPeriod: number | string;
    data: any;
    index: number;
    installment: string;
    interest: string;
    sumOfInterest: string;
    variableAmount: boolean;
    variableIRate: boolean;
    variableDue: boolean;
    monthlyData: boolean;
    extraAmountOrPayment: number;
    setExtraAmountOrPayment: (newExtraAmountOrPayment: string, index: number) => void;
    iRate: any;
    setNewIRate: (newIRate: number, index: number) => void;
    deleteIrate: (index: number) => void;
    due: any;
    setNewDue: (newDue: number, index: number) => void;
    notesOn: boolean;
    note: string;
    setNote: (note: string, index: number) => void;
    startingYear: number;
    setStartingYear: (newStartingYear: number) => void;
    edited?: boolean;
    recomputeDue?: (index: number) => void;
    editedFields?: string[];
};

export const AnnuityCalcTableRow = observer((props: AnnuityCalcTableRowProps) => {
    const {
        nrOfPeriod, 
        data, 
        installment,
        interest,
        sumOfInterest,
        variableAmount,
        variableIRate, 
        variableDue, 
        extraAmountOrPayment,
        setExtraAmountOrPayment,
        monthlyData,
        iRate, 
        setNewIRate,
        deleteIrate,
        due, 
        setNewDue,
        index,
        notesOn,
        note,
        setNote,
        edited,
        startingYear,
        setStartingYear,
    } = props;
    const [noteEditMode, setNoteEditMode] = useState(false);

    const inputRef = useRef<ElementRef<'textarea'>>(null);
    const extraAmountOrPaymentRef = useRef<ElementRef<'input'>>(null);

    const resizeInput = useCallback(() => {
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.style.setProperty('height', 'auto');
            inputRef.current.style.setProperty('height', inputRef.current.scrollHeight + 'px');
            }
        }, 0);
    }, [inputRef]);

    function tooltipsSection() {
        return <>
            <ReactTooltip
                id="auto-calc-due"
                place="top"
                content="Změnit splatnost tak, aby byla zachována výše splátky"
                className="tooltip"
            />
            <ReactTooltip
                id="remove-changes"
                place="top"
                content="Vymazat všechny změny pro toto období"
                className="tooltip"
            />
            <ReactTooltip
                id="toggle-expanded-show"
                place="top"
                content="Zobrazit jednotlivé měsíce"
                className="tooltip"
            />
            <ReactTooltip
                id="toggle-expanded-hide"
                place="top"
                content="Skrýt jednotlivé měsíce"
                className="tooltip"
            />
            <ReactTooltip
                id="edit-note"
                place="top"
                content="Upravit poznámku"
                className="tooltip"
            />
        </>
    }

    return <>
        <tr key={index} className= {`${edited ? "edited-bg-color" : ""}  ${(index % 24 > 12 || index % 24 === 0) ? "bg-gray-100/50" : "bg-white"} h-[30px] border-b border-gray-300`}>
            {
            index === 0 ? <td>
                <div className="h-7 flex items-center justify-center">
                    <span className="print-visible">{startingYear}{startingYear ? "/1" : ""}</span>
                    <span className="print-hidden">
                        <input type="number"
                            className="text-center input-number-no-arrows"
                            step={1}
                            min={0}
                            defaultValue={startingYear}
                            onBlur={(e) => {
                                const newStartingYear = e.target.value;
                                setStartingYear(sanitizeInput(newStartingYear));
                            }}
                            onKeyDown={(e: any) => {
                                if (e.key === 'Enter') {
                                    e.target.blur();
                                }
                            }}
                        />
                    </span>
                </div>
            </td>
            :
                <td
                    className="year-expand-toggle"
                    onClick={() => {
                        props.toggleExpanded && props.toggleExpanded()}
                    }
                >
                    <div className="h-7 flex items-center justify-center">
                        {props.toggleExpanded ? props.yearExpanded ? 
                            <ArrowUp className="absolute left-1 top-2 size-3 print-hidden" data-tooltip-id="toggle-expanded-hide" /> :
                            <ArrowDown className="absolute left-1 top-2 size-3 print-hidden" data-tooltip-id="toggle-expanded-show" /> : ''}
                        {typeof nrOfPeriod === 'number' ? Math.ceil(nrOfPeriod) + (Math.max(0, startingYear-1)) : nrOfPeriod}
                    </div>
                </td>
            }
            <td>{numberToStringWithSpaces(Math.floor(data.amount || 0))} Kč</td>
            <td>{installment} Kč</td>
            <td>{interest} Kč</td>
            <td>{sumOfInterest}</td>
            {variableAmount && <td>
                <span className="print-visible">{extraAmountOrPayment} Kč</span>
                <span className="print-hidden">
                    <input type="number" ref={extraAmountOrPaymentRef}
                        className={"px-2 !w-[90px]"}
                        step={50000}
                        max={data.amount || 0}
                        defaultValue={extraAmountOrPayment}
                        onBlur={(e) => {
                            const newExtraAmountOrPayment = e.target.value;
                            setExtraAmountOrPayment(newExtraAmountOrPayment, index);
                        }}
                        onKeyDown={(e: any) => {
                            if (e.key === 'Enter') {
                                e.target.blur();
                            }
                        }}
                    />
                </span>
                {
                    (extraAmountOrPayment !== 0 && index !== 0) && (<>
                    <span
                        className="print-hidden pointer calc-icon"
                        data-tooltip-id="auto-calc-due"
                        onClick={() => props.recomputeDue && props.recomputeDue(index)}
                    >
                        <Calculator/>
                    </span>
                    {/*
                    <span className="remove-changes-icon print-hidden">
                        <X className="pointer size-25" onClick={
                            () => {
                                setExtraAmountOrPayment("0", index);
                                if (extraAmountOrPaymentRef.current) {
                                    extraAmountOrPaymentRef.current.value = "0";
                                }
                            }
                        }/>
                    </span>
                    */}
                    </>)
                }
            </td>}
            {variableIRate && <td>
                <span className="print-visible">{iRate} %</span>
                <span className="print-hidden">
                    <input type="number"
                        className="px-2"
                        defaultValue={iRate}
                        step={0.1}
                        min={0}
                        onBlur={(e) => {
                            setNewIRate(sanitizeInput(e.target.value), index);
                        }}
                        onKeyDown={(e: any) => {
                            if (e.key === 'Enter') {
                                e.target.blur();
                            }
                        }}
                    />
                </span>
                {/*
                    props.editedFields?.some(field => field === "iRates") && <span className="remove-changes-icon print-hidden">
                        <X className="pointer size-25" onClick={() => deleteIrate(index)}/>
                    </span>
                */}
            </td>}
            {variableDue && <td>
                <span className="print-visible">
                    {Math.floor(Math.ceil(due*100)/(100 * 12))}
                    {
                        (Math.ceil(due*100)/100)%12 === 0 ? ' let' : ` + ${Math.ceil((due*100)/100 % 12)}`
                    }
                </span>
{
//                <span className="print-visible">{Math.ceil(Math.ceil(due*100)/100)} {monthlyData ? "měsíců" : "let"}</span>
}
                <span className={`print-hidden due-input-smaller`}>
                    {
                    // Years input
                    }
                    <input type="number"
                        className="px-2"
//                        defaultValue={Math.ceil(Math.ceil(due*100)/100)}
                        defaultValue={Math.max(Math.floor(due / 12), 0)}
                        step={1}
                        min={1}
                        max={40}
                        onBlur={(e) => {
                            const newWholeDue = sanitizeInput(Number(e.target.value)*12);
                            const newDue = newWholeDue + (((due/12) % 1) * 12);
                            setNewDue(newDue, index);
                        }}
                        onKeyDown={(e: any) => {
                            if (e.key === 'Enter') {
                                e.target.blur();
                            }
                        }}
                    />
                </span>
                {/*
                    props.editedFields?.some(field => field === "dues") && <span className="remove-changes-icon print-hidden extra-mr">
                        <X className="pointer size-25" onClick={() => {
                            setNewDue(data.due, index);
                            props.editedFields = [];
                        }}/>
                    </span>
                */}
                {
                    // Months input
                }
                <span className="print-hidden due-input-smallest">
                    <input type="number"
                        className="px-2"
//                        defaultValue={Math.ceil(((Math.floor(due*100)/100) % 1)*12)}
                        defaultValue={Math.ceil(due) % 12}
                        step={1}
                        min={0}
                        max={11}
                        onBlur={(e) => {
                            const newDue = sanitizeInput(e.target.value) + Math.floor(due - (due % 12));
                            setNewDue(newDue, index);
                        }}
                        onKeyDown={(e: any) => {
                            if (e.key === 'Enter') {
                                e.target.blur();
                            }
                        }}
                    />
                </span>

                {
                    (props.editedFields?.length && props.editedFields.length > 0 && props.editedFields.some(field => (field === "dues" || field === "iRates" || field === "extraAmountsOfPayments"))) &&
                    <span className="remove-changes-icon print-hidden extra-mr pointer">
                        <X data-tooltip-id="remove-changes" className="pointer size-[25px] relative top-[5px] right-[2px]" onClick={() => {
                            setExtraAmountOrPayment("0", index);
                            if (extraAmountOrPaymentRef.current) {
                                extraAmountOrPaymentRef.current.value = "0";
                            }
                            setNewDue(data.due, index);
                            deleteIrate(index);
                        }}/>
                    </span>
                }
            </td>}

            {(notesOn) && <td className="print:hidden">
                {!noteEditMode ? 
                    <PencilSquare className="pointer print-hidden" onClick={() => {
                        setNoteEditMode(true);
                        resizeInput();
                    }}
                    data-tooltip-id="edit-note"
                    />
                    :
                    <>
                        <Check className="pointer size-25 print-hidden" onClick={() => setNoteEditMode(false)}/>
                        <X className="pointer size-25 print-hidden" onClick={() => {
                            setNote("", index);
                            if (inputRef.current) {
                                inputRef.current.value = "";
                            }
                            setNoteEditMode(false);
                            }
                        }/>
                    </>
                }
            </td>
            }
        </tr>
        {
            (notesOn) && <tr key={index+'_notes'} className="print:hidden">
                {noteEditMode ?
                    <td colSpan={5 + (variableDue ? 1 : 0) + (variableAmount ? 1: 0) + (variableIRate ? 1:0)}>
                        <textarea
                        ref={inputRef}
                        className="note-textarea p-5 autoresize"
                        defaultValue={note}
                        onBlur={(e) => setNote(e.target.value, index)}
                        onChange={resizeInput}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (inputRef.current) {
                                inputRef.current.value += '\n';
                              }
                              resizeInput();
                          }
                        }}
                        />
                    </td>
                    :
                    (note && 
                        <td colSpan={4 + (variableDue ? 1 : 0) + (variableAmount ? 1: 0) + (variableIRate ? 1:0)}>
                            <span className="note">
                                {note}
                            </span>
                        </td>
                    )
                }
            </tr>
        }
        {tooltipsSection()}
    </>
});